// PM flow on the service call page: list machines at venue and open assessment per machine.
import React, { useState, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Snackbar,
  Alert,
  Box,
  Stack,
  Paper,
  Chip,
  Divider,
} from "@mui/material";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import { useDataContext } from "../contexts/DataContext";
import { req } from "../utils";
import { getJobVenueId, getMachinesAtVenueForJob } from "../utils/jobMachines";
import PmMachineAssessment from "./PmMachineAssessment";

export default function PerformPm({ job, triggerVariant = "contained", triggerSx = {} }) {
  const { machines, setMachines } = useDataContext();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [machineForAssessment, setMachineForAssessment] = useState(null);

  const venueMachines = useMemo(
    () => getMachinesAtVenueForJob(job, machines),
    [job, machines]
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handlePerformPm = (machine) => {
    if (!machine || !job?.service_call_id) return;
    setMachineForAssessment(machine);
    setAssessmentModalOpen(true);
  };

  const handleAssessmentComplete = async (
    machine,
    assessmentData,
    assessmentResponse
  ) => {
    setIsSubmitting(true);
    try {
      if (assessmentResponse && assessmentResponse.success === true) {
        const response = await req("post", "notify_pm_done", {
          machine_id_pk: machine.machine_id_pk,
          service_call_id: job.service_call_id,
        });

        if (response === true) {
          const currentDate = new Date().toISOString().split("T")[0];
          setMachines((prevMachines) =>
            prevMachines.map((m) =>
              m.machine_id_pk === machine.machine_id_pk
                ? { ...m, last_service_date: currentDate }
                : m
            )
          );
          handleClose();
          const assessmentId = assessmentResponse.assessment_id
            ? ` (Assessment ID: ${assessmentResponse.assessment_id})`
            : "";
          setSuccessMessage(
            `PM completed successfully for machine ${machine.serial_id}${assessmentId}`
          );
          setShowSuccessToast(true);
        } else {
          setErrorMessage(
            `PM assessment recorded but failed to update PM status for machine ${machine.serial_id}`
          );
          setShowErrorToast(true);
        }
      } else {
        const errorMsg =
          assessmentResponse?.message || "Failed to record PM assessment";
        setErrorMessage(`Assessment failed: ${errorMsg}`);
        setShowErrorToast(true);
      }
    } catch (error) {
      console.error("Error performing PM:", error);
      setErrorMessage(`Error completing PM for machine ${machine.serial_id}`);
      setShowErrorToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssessmentClose = () => {
    setAssessmentModalOpen(false);
    setMachineForAssessment(null);
    setShowErrorToast(false);
    setErrorMessage("");
  };

  const venueLabel = job?.venue_name || "this venue";

  return (
    <>
      <Button
        onClick={handleOpen}
        fullWidth
        variant={triggerVariant}
        color="persiangreen"
        sx={{ textTransform: "none", ...triggerSx }}
      >
        Perform PM
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <BuildCircleOutlinedIcon color="primary" sx={{ color: "var(--bc-persiangreen, #00A49B)" }} />
            <Box>
              <Typography component="span" variant="h6" display="block">
                Preventive maintenance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {venueLabel}
                {getJobVenueId(job) ? (
                  <span> · Venue ID {getJobVenueId(job)}</span>
                ) : null}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Machines on this call / venue
          </Typography>
          {venueMachines.length > 0 ? (
            <Stack spacing={2}>
              {venueMachines.map((machine, index) => (
                <Paper
                  key={machine.machine_id_pk || index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {machine.serial_id || "—"}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        {machine.machine_type ? (
                          <Chip size="small" label={machine.machine_type} variant="outlined" />
                        ) : null}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Last service:{" "}
                        <Box component="span" fontWeight={500} color="text.primary">
                          {machine.last_service_date || "Not recorded"}
                        </Box>
                      </Typography>
                      {machine.pm_due ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          PM due:{" "}
                          <Box component="span" fontWeight={500} color="text.primary">
                            {machine.pm_due}
                          </Box>
                        </Typography>
                      ) : null}
                    </Box>
                    <Button
                      variant="contained"
                      color="persiangreen"
                      disabled={isSubmitting}
                      onClick={() => handlePerformPm(machine)}
                      sx={{ alignSelf: { xs: "stretch", sm: "center" }, whiteSpace: "nowrap" }}
                    >
                      {isSubmitting ? "Processing…" : "Start PM assessment"}
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No machines found at this venue.
            </Typography>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessToast(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorToast}
        autoHideDuration={5000}
        onClose={() => setShowErrorToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowErrorToast(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {machineForAssessment && (
        <PmMachineAssessment
          machine={machineForAssessment}
          job={job}
          onAssessmentComplete={handleAssessmentComplete}
          onClose={handleAssessmentClose}
          open={assessmentModalOpen}
        />
      )}
    </>
  );
}
